import {
    CustomProjectEntity,
    RawCustomProject,
} from '@entities/CustomProject/Types';
import { CustomProjectsGateway } from '../Processes/Gateways/CustomProjects/CustomProjectsGateway';
import { useProjectStore } from '../Stores/ProjectStore';
import { CustomProjectModel } from '@entities/CustomProject/CustomProjectModel';
import { ApiResult } from '@shared/Interfaces/Api/ApiResult';
import { useGoogleMapViewModel } from '@viewModels/GoogleMap/GoogleMapViewModel';
import { useFragmentsStore } from '../Stores/FragmentsStore';
import { TOMORROW_DATE } from '@shared/Constants/TomorrowDate';
import { ParsedFragmentEntity } from '@entities/ParsedFragment/Types';
import { SetStoreByCoordinatesGateway } from '../Processes/Gateways/SetStoreByCoordinates/SetStoreByCoordinatesGateway';

const projectGateway = new CustomProjectsGateway();
const setStoreByCoordinatesGateway = new SetStoreByCoordinatesGateway();
const customProjectModel = new CustomProjectModel();

export class CustomProjectInfoViewModel {
    updateCustomProject = (
        key: keyof CustomProjectEntity,
        value: string
    ): void => {
        const { setCustomProject, customProject } = useProjectStore.getState();
        const updatedProject: CustomProjectEntity = {
            ...customProject,
            [key]: value,
        };
        setCustomProject(updatedProject);
    };

    updateCustomProjectAddress = async (projectId: number) => {
        const { address, markerPosition } = useGoogleMapViewModel.getState();
        if (!address || !markerPosition?.lat || !markerPosition.lng) {
            return { error: true, message: 'Address is required' };
        }

        await setStoreByCoordinatesGateway.sendCurrentAddress(
            String(markerPosition?.lng),
            String(markerPosition?.lat)
        );

        return projectGateway.updateAddress(
            projectId,
            address,
            String(markerPosition?.lat),
            String(markerPosition?.lng)
        );
    };

    async fetchUpdateCustomProject(): Promise<ApiResult<RawCustomProject>> {
        const { customProject, setProjectUpdateLoading } =
            useProjectStore.getState();

        if (
            !customProject.targetDate ||
            customProject.targetDate < TOMORROW_DATE
        ) {
            return {
                error: true,
                message:
                    "The chosen project target date is invalid. It must be at least tomorrow's date",
            };
        }

        setProjectUpdateLoading(true);
        try {
            const updatedProjectResponse =
                await projectGateway.updateProjectById(customProject);

            if (updatedProjectResponse.error) {
                return { error: true, message: 'Failed to update project' };
            }

            const updateAddressResponse = await this.updateCustomProjectAddress(
                customProject.id
            );
            if (updateAddressResponse.error || !updateAddressResponse.data) {
                return {
                    error: true,
                    message:
                        updateAddressResponse.message ??
                        'Failed to update project address',
                };
            }

            const customProjectModel = new CustomProjectModel();
            customProjectModel.initialize(updateAddressResponse.data);

            useProjectStore.setState({
                customProject: customProjectModel,
            });

            return updateAddressResponse;
        } finally {
            setProjectUpdateLoading(false);
        }
    }

    async getCustomProject(
        projectId: number
    ): Promise<ApiResult<RawCustomProject>> {
        const { setProjectLoading } = useProjectStore.getState();
        setProjectLoading(true);
        try {
            const response =
                await projectGateway.getCustomProjectById(projectId);

            if (!response.error && response.data) {
                customProjectModel.initialize(response.data);
                useProjectStore.setState({ customProject: customProjectModel });
            }

            return response;
        } finally {
            setProjectLoading(false);
        }
    }

    getExampleProject = (project: RawCustomProject) => {
        customProjectModel.initialize(project);
        const fragments = customProjectModel!.templates![0].fragments;
        useProjectStore.setState({
            customProject: customProjectModel,
        });
        useFragmentsStore.setState({
            parsedFragments: fragments as unknown as ParsedFragmentEntity[],
        });
    };

    async createNewProjectWithFreeTemplate(
        templateIds: number[]
    ): Promise<ApiResult<RawCustomProject>> {
        const { setCustomProject, setProjectLoading } =
            useProjectStore.getState();
        setProjectLoading(true);
        const response =
            await projectGateway.createCustomProjectWithFreeTemplate(
                templateIds
            );

        if (response.error || !response.data) {
            setProjectLoading(false);
            return {
                error: true,
                message: 'Failed to add template to new project',
            };
        } else {
            customProjectModel.initialize(response.data);
            setCustomProject(customProjectModel);
            setProjectLoading(false);
            return response;
        }
    }
}
